import { node, shape, string } from "prop-types";
import { createContext, useContext, useMemo } from "react";

import { PRODUCT_TYPE } from "~config/shop";

/**
 * @typedef {Object} ContentRoutes
 * @property {string|null} cityCardProductSelectorPageRoute
 * @property {string|null} canalCruiseProductSelectorPageRoute
 * @property {string|null} cityCardCheckoutPageRoute
 * @property {string|null} canalCruiseCheckoutPageRoute
 * @property {string|null} loginPageRoute
 * @property {string|null} accountPageRoute
 * @property {string|null} accountSettingsPageRoute
 */

const ContentRoutesContext = createContext(null);

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {ContentRoutes} props.contentRoutes
 * @returns {React.ReactNode}
 */
export const ContentRoutesProvider = ({ children, contentRoutes }) => {
  return (
    <ContentRoutesContext.Provider value={contentRoutes}>
      {children}
    </ContentRoutesContext.Provider>
  );
};

ContentRoutesProvider.propTypes = {
  children: node.isRequired,
  contentRoutes: shape({
    cityCardProductSelectorPageRoute: string,
    canalCruiseProductSelectorPageRoute: string,
    cityCardCheckoutPageRoute: string,
    canalCruiseCheckoutPageRoute: string,
    loginPageRoute: string,
  }).isRequired,
};

/**
 * @returns {ContentRoutes & {
 *   getProductSelectorPageRoute: (productType: "citycard" | "canalcruise") => string | null,
 *   getCheckoutPageRoute: (productType: "citycard" | "canalcruise") => string | null,
 * }}
 */
export const useContentRoutes = () => {
  const contentRoutes = useContext(ContentRoutesContext);
  if (!contentRoutes) {
    throw new Error("ContentRoutesContext not found");
  }
  const withHelpers = useMemo(
    () => ({
      ...contentRoutes,
      getProductSelectorPageRoute: (productType) => {
        switch (productType) {
          case PRODUCT_TYPE.CITY_CARD:
            return contentRoutes.cityCardProductSelectorPageRoute;
          case PRODUCT_TYPE.CANAL_CRUISE:
            return contentRoutes.canalCruiseProductSelectorPageRoute;
          default:
            return null;
        }
      },
      getCheckoutPageRoute: (productType) => {
        switch (productType) {
          case PRODUCT_TYPE.CITY_CARD:
            return contentRoutes.cityCardCheckoutPageRoute;
          case PRODUCT_TYPE.CANAL_CRUISE:
            return contentRoutes.canalCruiseCheckoutPageRoute;
          default:
            return null;
        }
      },
    }),
    [contentRoutes],
  );
  return withHelpers;
};

export default ContentRoutesContext;
